import { RouteUiService } from '../../../stores/ui/route-ui.service';
import { Models } from '../../../models/global.models';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SecurityProfileRole } from 'apps/cmg-clear2-ui/src/app/loan-state/security-profile';
import { NotificationService } from 'apps/cmg-clear2-ui/src/app/services';

export const loProfiles = [
  SecurityProfileRole.LoanOfficer,
  SecurityProfileRole.CallCenterLO,
  SecurityProfileRole.DisclosureLO,
  SecurityProfileRole.SuperDisclosureLO,
  SecurityProfileRole.SuperDisclosureLORelo,
];

export const securityProfilesReadonlyAccess = [
  SecurityProfileRole.AccountExecutive,
  SecurityProfileRole.AppraisalDesk,
  SecurityProfileRole.Closer,
  SecurityProfileRole.Compliance,
  SecurityProfileRole.DisclosureCoordinator,
  SecurityProfileRole.Funder,
  SecurityProfileRole.ReDisclosureCoordinator,
  SecurityProfileRole.TeamLead,
  SecurityProfileRole.RegistrationCoordinator,
  SecurityProfileRole.Underwriter,
  // not in clear
  SecurityProfileRole.AccountManager,
  SecurityProfileRole.BBLoanOfficer,
  SecurityProfileRole.BBRetailOpsManager,
  SecurityProfileRole.BBUnderwriter,
  SecurityProfileRole.CORRClientLiaison,
  SecurityProfileRole.CollateralSpecialist,
  SecurityProfileRole.CORRCreditAuditor,
  SecurityProfileRole.ClientSuccessExpert,
  SecurityProfileRole.Insuring,
  SecurityProfileRole.CORRLegalAuditor,
  SecurityProfileRole.LockDesk,
  SecurityProfileRole.PostCloserMERS,
  SecurityProfileRole.CORRPurchaser,
  SecurityProfileRole.QualityControl,
  SecurityProfileRole.Secondary,
  SecurityProfileRole.Servicing,
  SecurityProfileRole.Shipper,
  SecurityProfileRole.WarehouseCoordinator,
];

// The following profiles will not have access to the Partners Pipeline, but will still have access to modify
// Co-Branded REA on loan applications currently visible to them within their organization/branch
export const securityProfilesAllowedWithoutPipelineAccess = [
  SecurityProfileRole.RetailOpsManager,
  SecurityProfileRole.SeniorOpsManager,
  SecurityProfileRole.SVP,
  SecurityProfileRole.LoanProcessor,
  SecurityProfileRole.Processor2,
  SecurityProfileRole.Processor3,
  ...securityProfilesReadonlyAccess,
];

export const addPartnerErrors = {
  associatedWithActivePartner: 'Partner already added to pipeline',
  associatedWithInactivePartner:
    'Partner already added to pipeline with co-branding deactivated. Please activate co-branding on this partner in the Inactive partners list to add this partner.',
};

export const showPartnersDrawer = (
  partner: Models.PipelinePartner,
  isInvite: boolean,
  routeUiService: RouteUiService,
  notificationService: NotificationService,
) => {
  if (isInvite) {
    setTimeout(() => {
      routeUiService.stateChange({
        sidebarVisible: true,
        quickActionSelection: Models.QuickActionType.InvitePartners,
        sidebarGridData: partner,
      });
    }, 0);
  } else if (partner?.coBranding === 'Active') {
    routeUiService.stateChange({
      sidebarVisible: true,
      quickActionSelection: Models.QuickActionType.EditPartner,
      sidebarGridData: partner,
    });
  } else {
    notificationService.showToast({
      severity: 'error',
      detail: 'Partner co-branding must be active to edit details',
    });
  }
};

// Must have text before and after @
// Must include a dot . after the domain
// Top-level domain must be at least 2 characters
// Characters in email may only include letters, digits, and the following special chars
// @ ! # $ % & ' * + - / = ? ^ _ ` . { | } ~
export const oktaEmailValidator = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;

    if (!value) {
      return null; // don't validate empty value (use Validators.required for that)
    }

    const invalidCharsRegex = /[^a-zA-Z0-9@!#$%&'*+\-/=?^_`.{|}~]/;

    const validOktaEmailChars = "[A-Za-z0-9@!#$%&'*+\\-/=?^_`.{|}~]";
    const emailRegex = new RegExp(`^${validOktaEmailChars}+@${validOktaEmailChars}+\\.${validOktaEmailChars}{2,}$`);
    const isValid = emailRegex.test(value);
    const hasInvalidOktaChars = invalidCharsRegex.test(value);

    if (hasInvalidOktaChars) {
      // Not displayed as inline validation. Instead invalidOktaChars error used to show/hide custom error banner.
      return { invalidOktaChars: 'Invalid characters' };
    } else if (!isValid) {
      // Displayed as inline validation.
      return { cmgValidationError: 'Invalid email format' };
    }

    return null;
  };
};

/**
 * Validates that control value includes only upper/lowercase letters, spaces, periods and hyphens.
 */
export const licenseNumberValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const isWhitespace = (value || '').trim().length === 0;
    const regex = /^[A-Za-z0-9 .-]*$/;

    if (!isWhitespace && !regex.test(value)) {
      return { cmgValidationError: 'Special characters in License Number can only include spaces, periods, and hyphens' };
    }

    return null;
  };
};
